// https://developer.atlassian.com/platform/ufo/ufo-core/intro-and-basics/taxonomy/

import { ExperiencePerformanceTypes, ExperienceTypes } from "@atlassian/ufo";

import { UserProfileResponse } from "@/libraries/bxp-web-stargate-client/src";

import { ConfirmationPageType } from "../../../templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-confirmation-page-type";

import { StatsigExperimentKey } from "./../../../feature-gates/types";
import { OrgBasedProduct, ProductKey } from "./products";

export const UfoEventName = {
    /**
     * Land Step — User can view signup pages
     * - Tome SLOs: https://tome.prod.atl-paas.net/capability/4cba7cce-f259-4957-b071-297d12f2a2e2
     **/
    ENTER_SIGNUP_FLOW_SLO_SUCCEEDED: "enter-signup-flow.slo-succeeded",
    ENTER_SIGNUP_FLOW_SLO_FAILED: "enter-signup-flow.slo-failed",

    /**
     * Land Step — User can submit to start site and/or product activation
     * - Tome SLOs: https://tome.prod.atl-paas.net/capability/5eb87129-93bf-4299-bb87-647274daab95
     **/
    // Default submit captured across bxpui and bxpkit
    DEFAULT_SIGNUP_SUBMIT_SLO_SUCCEEDED: "default-signup-submit.slo-succeeded",
    DEFAULT_SIGNUP_SUBMIT_SLO_FAILED: "default-signup-submit.slo-failed",
    BXPUI_SIGNUP_SUBMIT_SLO_SUCCEEDED: "bxpui-signup-submit.slo-succeeded",
    BXPUI_SIGNUP_SUBMIT_SLO_FAILED: "bxpui-signup-submit.slo-failed",
    BXPKIT_SIGNUP_SUBMIT_SLO_SUCCEEDED:
        "bxpkit-signup-form-submit.slo-succeeded",
    BXPKIT_SIGNUP_SUBMIT_SLO_FAILED: "bxpkit-signup-form-submit.slo-failed",

    /* Events */
    ACTIVATE_PRODUCT_API_SUCCESS: "activate-product-api.success",
    ACTIVATE_PRODUCT_API_ERROR: "activate-product-api.error",

    /**
     * Experiment - Confluence set up over provisioning
     * - statsig setup: https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/cfind-1054_confluence_set_up_over_provisioning/setup
     */
    CONFLUENCE_SETUP_OVER_SITE_PROVISIONING_INVITE_LINK_FETCH_ERROR:
        "cfind-1054-confluence-set-up-over-provisioning.invite-link-fetch-error",
    CONFLUENCE_SETUP_OVER_SITE_PROVISIONING_ONBOARDING_RESPONSE_RECEIVED:
        "cfind-1054-confluence-set-up-over-provisioning.onboarding-response-received",
    CONFLUENCE_SETUP_OVER_SITE_PROVISIONING_ONBOARDING_RESPONSE_MISSING:
        "cfind-1054-confluence-set-up-over-provisioning.onboarding-response-missing",
    CONFLUENCE_SETUP_OVER_SITE_PROVISIONING_ONBOARDING_RESPONSE_ERROR:
        "cfind-1054-confluence-set-up-over-provisioning.onboarding-response-error"
} as const;

export type UfoEventName = (typeof UfoEventName)[keyof typeof UfoEventName];

// hello.atlassian.net/wiki/spaces/~lhunt/blog/2024/01/22/3243354933/How+Removing+and+Replacing+TypeScript+Enums+improves+Code
export const UfoExperienceStatus = {
    SUCCEEDED: "SUCCEEDED",
    ABORTED: "ABORTED"
} as const;

export type UfoExperienceStatus =
    (typeof UfoExperienceStatus)[keyof typeof UfoExperienceStatus];

export const UfoInteractionType = {
    PAGE_LOAD: "page-load",
    PRESS: "press",
    TYPING: "typing",
    TRANSITION: "transition",
    CUSTOM: "custom"
} as const;

export type UfoInteractionType =
    (typeof UfoInteractionType)[keyof typeof UfoInteractionType];

export type UfoLabelStack = { name: string }[];

export const UfoCustomTimingKey = {
    STARGATE_AVAILABLE_PRODUCTS_API: "stargate_available_products_api",
    STARGATE_CONSENT_CONFIG_API: "stargate_consent_config_api",
    STARGATE_EXPAND_FREE_VALIDATION_API: "stargate_expand_free_validation_api",
    STARGATE_USER_API: "stargate_user_api",
    IDENTITY_USER_SIGNUP_API: "identity_user_signup_api",
    ACTIVATE_STATUS_CCP_POLLING_API: "activate_status_ccp_polling_api",
    CLOUD_STATUS_HAMS_POLLING_API: "cloud_status_hams_polling_api",
    ROBOTS_VIEW_DWELL_TIME: "robots_view_dwell_time",
    CONFLUENCE_DEFAULT_CIRCLE_DWELL_TIME:
        "confluence_default_circle_dwell_time",
    CONFLUENCE_ONBOARDING_SPINNER_DWELL_TIME:
        "confluence_onboarding_spinner_dwell_time",
    CONFLUENCE_ONBOARDING_STEPS_DWELL_TIME:
        "confluence_onboarding_steps_dwell_time",
    JIRA_ANIMATED_PROVISIONING_DWELL_TIME:
        "jira_animated_provisioning_dwell_time",
    JIRA_ONBOARDING_WIZARD_PROJECT_CREATE_DWELL_TIME:
        "jira_onboarding_wizard_project_create_dwell_time",
    JIRA_TEMPLATE_CHOICE_DWELL_TIME: "jira_template_choice_dwell_time",
    JIRA_USER_SEGMENTATION_DWELL_TIME: "jira_user_segmentation_dwell_time",
    JIRA_PRODUCT_DISCOVERY_ONBOARDING_SPINNER_DWELL_TIME:
        "jira_product_discovery_onboarding_spinner_dwell_time",
    USER_SEGMENTATION_DWELL_TIME: "user_segmentation_dwell_time",
    TIPS_VIEW_DWELL_TIME: "tips_view_dwell_time",
    GRAPHQL_WEBHOOK_SETUP: "graphql_webhook_setup",
    GRAPHQL_SUBSCRIPTION_ACK: "graphql_subscription_ack",
    GRAPHQL_WEBHOOK_PROVISIONING_LATENCY:
        "graphql_webhook_provisioning_latency",
    ONBOARDING_WIZARD_PROJECT_CREATE_API_CALL:
        "onboarding-wizard.project-create-api",
    SERVICE_COLLECTION_PROVISIONING_VIEW_DWELL_TIME:
        "service_collection_provisioning_view_dwell_time",
    TWC_PROVISIONING_DWELL_TIME: "twc_provisioning_dwell_time",

    JIRA_ONBOARDING_WIZARD_INVITE_POLLING_PROJECT_CREATE_TASK:
        "JIRA_ONBOARDING_WIZARD_INVITE_POLLING_PROJECT_CREATE_TASK",
    JIRA_ONBOARDING_WIZARD_INVITE_FETCHING_ROLES:
        "jira_onboarding_wizard_invite_fetching_roles",
    JIRA_ONBOARDING_WIZARD_INVITE_CHECKING_LICENSE:
        "jira_onboarding_wizard_invite_checking_license",
    JIRA_ONBOARDING_WIZARD_INVITE_INVITING_USERS:
        "jira_onboarding_wizard_invite_inviting_users",
    RESOLVE_TENANT_ID: "resolve_tenant_id",
    JIRA_ONBOARDING_MADLIB_PROJECT_CREATE:
        "jira_onboarding_madlib_project_create",
    JIRA_ONBOARDING_DEFAULT_PROJECT_CREATE:
        "jira_onboarding_default_project_create"
} as const;

export type UfoCustomTimingKey =
    (typeof UfoCustomTimingKey)[keyof typeof UfoCustomTimingKey];

export type SignupUfoExperienceConfig = {
    type?: ExperienceTypes;
    performanceType?: ExperiencePerformanceTypes;
    customTimingKeys?: UfoCustomTimingKey[];
    productKey?: string;
    platform?: {
        component: string;
    };
};

export type SignupUfoExperienceConfigTimings = {
    key: UfoCustomTimingKey;
    startMark: string;
    endMark: string;
}[];

export type UfoExperienceConfigTimings = {
    key: string;
    startMark: string;
    endMark: string;
}[];

export type UfoExperienceConfig = {
    type: ExperienceTypes;
    performanceType: ExperiencePerformanceTypes;
    timings?: UfoExperienceConfigTimings;
    platform?: {
        component: string;
    };
};

/*
 * UFO Custom Cohorts for metric breakdown & logs
 */
export const UfoCustomDataProductCohort = {
    JIRA_SOFTWARE: "jira-software",
    CONFLUENCE: "confluence",
    JIRA_WORK_MANAGEMENT: "jira-work-management",
    JIRA_SERVICE_MANAGEMENT: "jira-service-management",
    JIRA_PRODUCT_DISCOVERY: "jira-product-discovery",
    OPSGENIE: "opsgenie",
    LOOM: "loom",
    OTHER: "other",
    NOT_FOUND: "not-found"
} as const;
export type UfoCustomDataProductCohort =
    (typeof UfoCustomDataProductCohort)[keyof typeof UfoCustomDataProductCohort];

export const UfoCustomDataSiteTypeCohort = {
    NEW_SITE_N2N: "new-site-n2n",
    EXISTING_SITE_N2E: "existing-site-n2e",
    NOT_FOUND: "not-found"
} as const;
export type UfoCustomDataSiteTypeCohort =
    (typeof UfoCustomDataSiteTypeCohort)[keyof typeof UfoCustomDataSiteTypeCohort];

export const UfoCustomDataBundleCohort = {
    DEVOPS: "devops",
    LOOM: "loom",
    JIRA_SERVICE_MANAGEMENT: "jira-service-management",
    NOT_FOUND: "not-found"
} as const;
export type UfoCustomDataBundleCohort =
    (typeof UfoCustomDataBundleCohort)[keyof typeof UfoCustomDataBundleCohort];

export const UfoCohortWebPlatform = {
    BXP_UI: "bxp-ui",
    BXPKIT: "bxpkit",
    UNKNOWN: "unknown"
} as const;
export type UfoCohortWebPlatform =
    (typeof UfoCohortWebPlatform)[keyof typeof UfoCohortWebPlatform];

export const UfoCustomDataEditionCohort = {
    FREE: "free",
    STANDARD: "standard",
    PREMIUM: "premium",
    UNKNOWN: "unknown"
} as const;
export type UfoCustomDataEditionCohort =
    (typeof UfoCustomDataEditionCohort)[keyof typeof UfoCustomDataEditionCohort];

export type SignupUfoCustomData = {
    // Base data
    isPerformancePortal?: boolean;
    reactComponent?: string;
    analyticsEvent?: string;
    browserPath?: string;
    redirectFrom?: string;
    webPlatform?: string;
    webPlatformCohort?: UfoCohortWebPlatform;
    redirectUrl?: string;
    createProjectStatus?: string;
    isEventualConsistency?: boolean;
    createProjectTemplateKey?: string;
    // User
    analyticsAnonymousId?: string;
    isSignupTraitsUserLoggedIn?: boolean;
    isSignupTraitsUserLandNew?: boolean;
    cloudId?: string;
    isLoggedIn?: boolean;
    cohorts?: UserProfileResponse["cohorts"];
    teamType?: string;
    template?: string;
    templateId?: string;
    // Product
    productKey?: ProductKey | OrgBasedProduct;
    product?: string;
    productCohort?: UfoCustomDataProductCohort;
    bundle?: string;
    bundleCohort?: UfoCustomDataBundleCohort;
    edition?: string;
    editionCohort?: string;
    productName?: string;
    accessStatus?: string;
    accessType?: string;
    bitbucketWorkspaceUuid?: string;
    migrationSourceUuid?: string;
    // Multiple Products
    bundleProductKeys?: string;
    // Provisioning
    provisioningStatus?: string;
    activationStatus?: string;
    provisioningRequestId?: string;
    finalProvisioningScreen?: ConfirmationPageType;
    orchestrationId?: string; // GraphQL Webhook Provisioning
    isInitialStatusPollingStatusOk?: boolean; // GraphQL initial status polling result
    signupId?: string;
    // Site
    siteType?: string;
    availability?: string;
    invalidTimes?: number;
    siteName?: string; // PII data?
    // Request
    atlTraceId?: string;
    reqPath?: string;
    affectedEndpoint?: string; // duplicate of reqPath?
    message?: string;
    statusCode?: number;
    elapsedTimeMs?: number;
    // Error
    errorName?: string;
    errorMessage?: string;
    errorStack?: string;
    errorStatusCode?: number;
    errorReqPath?: string;
    errorOrigin?: string;
    // Experiment
    experimentKey?: StatsigExperimentKey;
    experimentName?: string;
    experimentValue?: boolean;
    featureGateKey?: string;
    featureGateValue?: boolean;
    // Recaptcha
    gRecaptchaResponse?: string;
    executeRecaptchaExists?: boolean;
    recaptchaRetryTimeRemainingSecond?: number;
    // Anchor Journey
    anchorLocalStorage?: string;
    // Activation
    activationType?: string;
    // Organization
    orgId?: string;
    orderId?: string;
    transactionAccountId?: string;
    accountOrgs?: string;
    // Async Architecture
    workflowId?: string;
};
